// src/features/quality/pages/QualityCheckFamilyDetailPage.tsx

import { useNavigate, useParams } from "react-router-dom";
import { usePermission } from "@/auth/usePermission";
import { QualitySimpleTable } from "../components/QualitySimpleTable";
import { RevisionStatusBadge } from "../components/RevisionStatusBadge";
import type { QualityCheckFamily, QualityCheckRevision } from "../types";

// TODO(verify): confirm exact hook names after `npm run api:generate`.
// A per-family filter param on the revisions-list endpoint
// (?family=<id>) already exists (family is a normal FK filter field, per
// the existing v1QualityCheckRevisionsListParams.ts) — no backend change
// needed for this filter.
import {
  useV1QualityCheckFamiliesRetrieve,
  useV1QualityCheckRevisionsList,
} from "@/api/generated/v1/v1";

import { PageBanner } from "@/shared/components/PageBanner";

export function QualityCheckFamilyDetailPage() {
  const { familyId } = useParams<{ familyId: string }>();
  const navigate = useNavigate();
  const canManage = usePermission("quality.manage");

  const { data: familyResponse, isLoading: familyLoading } =
    useV1QualityCheckFamiliesRetrieve(familyId!);
  const { data: revisionsResponse, isLoading: revisionsLoading } =
    useV1QualityCheckRevisionsList({ family: familyId });

  const family: QualityCheckFamily | undefined = familyResponse;
  const revisions: QualityCheckRevision[] =
    revisionsResponse?.results ?? [];

  if (familyLoading) return <p className="p-6">در حال بارگذاری...</p>;
  if (!family) return <p className="p-6">یافت نشد.</p>;

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title={`${family.code} — ${family.name}`}
        subtitle="جزئیات برنامه کنترل کیفیت و مدیریت تجدیدنظرها"
        icon="🛡️"
        helpLink="/help/quality/quality-check-family-detail"
      >
          {canManage && (
            <button
              onClick={() => navigate(`/quality/families/${familyId}/revisions/new`)}
              style={{
                padding: "0.6rem 1.25rem",
                borderRadius: "12px",
                background: "#ffffff",
                color: "#007270",
                fontWeight: 700,
                fontSize: "0.9rem",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                transition: "all 0.2s ease",
              }}
            >
              <span>➕</span>
              <span>تجدیدنظر (Revision) جدید</span>
            </button>
          )}
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
        <h2 style={{ fontSize: "1.1rem", fontWeight: 800, color: "#007270", marginBottom: "1rem" }}>
          لیست تجدیدنظرها (Revisions)
        </h2>

        {revisionsLoading ? (
          <p className="text-muted">در حال بارگذاری...</p>
        ) : (
          <QualitySimpleTable<QualityCheckRevision>
            rows={revisions}
            rowKey={(r) => r.id}
            onRowClick={(r) => navigate(`/quality/revisions/${r.id}`)}
            emptyMessage="این برنامه هنوز هیچ Revisionی ندارد."
            columns={[
              {
                key: "revision_no",
                header: "شماره",
                render: (r) => `Rev ${r.revision_no}`,
              },
              { key: "name", header: "نام", render: (r) => r.name },
              {
                key: "status",
                header: "وضعیت",
                render: (r) => (
                  <RevisionStatusBadge
                    status={r.status as any}
                    approvalState={(r as any).approval_state}
                  />
                ),
              },
            ]}
          />
        )}
      </div>
    </div>
  );
}
